import { useCallback, useState } from 'react';
import { useSyncLocalStorage } from 'src/hooks/useSyncLocalStorage';

interface Params {
	/**
	 * Used to create the `localStorage` key.
	 */
	tableId: string;
	numItems: number;
}

/**
 * Manages the state for a list of items that can individually be expanded,
 * typically also accompanied by an "Expand All" / "Collapse All" button.
 */
export default function useExpandableItemList({ tableId, numItems }: Params) {
	const [areAllItemsExpanded, setAreAllItemsExpanded] = useSyncLocalStorage(
		`expand-all-${tableId}-table-cards`,
		false
	);
	const [numItemsExpanded, setNumItemsExpanded] = useState(() =>
		areAllItemsExpanded ? numItems : 0
	);

	const handleItemExpandedChange = useCallback(
		(expanded: boolean) => {
			setNumItemsExpanded((prev) => {
				const next = expanded ? prev + 1 : prev - 1;
				return Math.max(0, Math.min(numItems, next));
			});

			// Check if we also need to update "all expanded" state.
			// Either we've expanded the last item...
			if (expanded && numItemsExpanded === numItems - 1) {
				setAreAllItemsExpanded(true);
				return;
			}

			// Or we've collapsed the last item...
			if (!expanded && numItemsExpanded === 1) {
				setAreAllItemsExpanded(false);
			}
		},
		[numItems, numItemsExpanded]
	);

	const handleExpandAllItems = useCallback(() => {
		setNumItemsExpanded(numItems);
		setAreAllItemsExpanded(true);
	}, [numItems]);

	const handleCollapseAllItems = useCallback(() => {
		setNumItemsExpanded(0);
		setAreAllItemsExpanded(false);
	}, []);

	return {
		areAllItemsExpanded,
		handleItemExpandedChange,
		handleExpandAllItems,
		handleCollapseAllItems,
	};
}
